import { useQuery } from "@tanstack/react-query";
import getConfig from "next/config";
import { useDecision } from "@optimizely/react-sdk";
import { useGetScopedContentLegacy } from "@/common/MerchandiseBanner/hooks/useGetScopedContentLegacy";
import { ContentfulPage, MerchandiseBanner, PageContentQueryResult, PageData, PageDataResponse, SectionsComponent, UiScopedContent } from "@/common/MerchandiseBanner/types";

const { publicRuntimeConfig } = getConfig();
const {
    CONTENTFUL_SPACE_ID,
    CONTENTFUL_ENVIRONMENT,
    CONTENTFUL_ACCESS_TOKEN,
    CONTENTFUL_BASE_URL
} = publicRuntimeConfig;

const generateQueryLegacy = (page: ContentfulPage, scopedConditions: string[]) => {
    const scopedWhere = `where: { AND: [ ${scopedConditions.join(", ")} ] }`;

    return `
fragment bouncebackBannerContent on MerchandiseBanner {
  title
  altText
  targetUrl
  desktopImage {
    url
  }
  mobileImage {
    url
  }
}

query GetPageData {
  uiPageCollection(where: {name_contains: "[Web] ${page}"}){
    items {
      sectionsCollection {
        items {
          ... on UiScopedSection {
            __typename
            contentCollection(limit: 1, ${scopedWhere}) {
              items {
                ... on UiScopedContent {
                  __typename
                  content {
                    ...bouncebackBannerContent
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
    `
};

const generateQuery = (page: ContentfulPage, scopedConditions: string[]) => {
    const scopedWhere = `where: { AND: [ ${scopedConditions.join(", ")} ] }`;

    return `
fragment merchandiseBannerContent on MerchandiseBanner {
  __typename
  title
  altText
  targetUrl
  desktopImage {
    url
  }
  mobileImage {
    url
  }
}

query GetPageData {
  uiPageCollection(where: {name_contains: "[Web] ${page}"}){
    items {
      sectionsCollection {
        items {
          ... on UiScopedSection {
            __typename
            contentCollection(limit: 1, ${scopedWhere}) {
              items {
                ... on UiScopedContent {
                  __typename
                  content {
                    ...merchandiseBannerContent
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
    `
};

const fetchPageContentLegacy = async (page: ContentfulPage, scopedConditions: string[]): Promise<PageData> => {
    const query = generateQueryLegacy(page, scopedConditions);

    const response = await fetch(
        `${CONTENTFUL_BASE_URL}/spaces/${CONTENTFUL_SPACE_ID}/environments/${CONTENTFUL_ENVIRONMENT}`,
        {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${CONTENTFUL_ACCESS_TOKEN}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ query })
        }
    );

    const data: PageDataResponse = await response.json();
    const sectionsCollection = data?.data?.uiPageCollection?.items?.[0]?.sectionsCollection?.items || [];
    const contentCollection = sectionsCollection.flatMap((section: SectionsComponent) => section.contentCollection?.items || []);
    const bannerSection = contentCollection.find((section: UiScopedContent) => section?.content?.title?.includes('Bounceback'));
    const bouncebackBanner: MerchandiseBanner | undefined = bannerSection?.content;
    return { merchandiseBannerLegacyV2Data: bouncebackBanner };
};

const fetchPageContent = async (page: ContentfulPage, scopedConditions: string[]): Promise<PageData> => {
    const query = generateQuery(page, scopedConditions);

    const response = await fetch(
        `${CONTENTFUL_BASE_URL}/spaces/${CONTENTFUL_SPACE_ID}/environments/${CONTENTFUL_ENVIRONMENT}`,
        {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${CONTENTFUL_ACCESS_TOKEN}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ query })
        }
    );

    const data: PageDataResponse = await response.json();
    const sectionsCollection = data?.data?.uiPageCollection?.items?.[0]?.sectionsCollection?.items || [];
    const bannerSection = sectionsCollection.find((section: SectionsComponent) => section.contentCollection?.items?.length > 0 && section.contentCollection.items.some(item => item.__typename === 'UiScopedContent' && item.content?.__typename === 'MerchandiseBanner'));
    // index 0 is used here because we expect only one merchandise banner per page based on the query structure limit 1
    const merchandiseBannerScopedUI = bannerSection?.contentCollection?.items?.[0];
    const merchandiseBannerLegacyV2Data = merchandiseBannerScopedUI?.content;
    return { merchandiseBannerLegacyV2Data };
};

export function usePageContentLegacy(page: ContentfulPage): PageContentQueryResult {
    const scopedConditions = useGetScopedContentLegacy();
    const [{ enabled: newMerchBannerLogicEnabled}] = useDecision('fr-dtg-1693-merch-banner-more-generic')

    return useQuery({
        queryKey: ['pageDataLegacy', page, scopedConditions],
        queryFn: () => newMerchBannerLogicEnabled ? fetchPageContent(page, scopedConditions) : fetchPageContentLegacy(page, scopedConditions)
    })
};